import styled, { css } from 'styled-components';

import { breakpoints, Heading, Link, theme } from '@notino/react-styleguide';

import { CommonContainer, CommonMarginWrapper } from '../../styled';

export const Container = styled(CommonContainer)<{
  newPdDesignEnabled: boolean;
}>`
  ${({ newPdDesignEnabled }) =>
    newPdDesignEnabled
      ? css`
          flex-wrap: wrap;
          @media (min-width: ${breakpoints.lg}) {
            flex-flow: row;
          }
          p,
          ul {
            color: ${theme.color.text.secondary};
            strong {
              color: ${theme.color.text.primary};
            }
          }
        `
      : css`
          flex-wrap: wrap;
        `}
`;

interface IFullWidth {
  fullWidth: boolean;
  newPdDesignEnabled?: boolean;
}

export const MarginWrapper = styled(CommonMarginWrapper)`
  ${({ newPdDesignEnabled, fullWidth }: IFullWidth) =>
    !newPdDesignEnabled &&
    css`
      @media (min-width: ${breakpoints.md}) {
        flex-basis: ${fullWidth ? '100%' : '66%'};
        width: ${fullWidth ? '100%' : '66%'};
        padding-right: ${fullWidth ? '0' : '2rem'};
      }
    `}

  @media screen and (-ms-high-contrast: active),
    (-ms-high-contrast: none) and (min-width: ${breakpoints.md}) {
    flex-basis: ${(props: IFullWidth) =>
      props.fullWidth ? '100%' : 'calc(66% - 2rem)'};
    width: ${(props: IFullWidth) =>
      props.fullWidth ? '100%' : 'calc(66% - 2rem)'};
  }
`;

export const CharTitle = styled(Heading.H2)<{ newPdDesignEnabled: boolean }>`
  text-align: left !important;
  display: flex;
  align-items: center;
  justify-content: space-between;

  cursor: pointer;
  flex-basis: 100%;

  ${({ newPdDesignEnabled }) =>
    newPdDesignEnabled
      ? css`
          margin: 0;
          margin-bottom: 1rem;
          ${theme.typography.titleMedium}
          @media (min-width: ${breakpoints.lg}) {
            display: none;
          }
        `
      : css`
          margin: 0.625rem 0;
          font-size: 1.125rem;
          font-weight: bold;
          @media (min-width: ${breakpoints.md}) {
            display: none;
          }
        `}
`;

export const Description = styled.div<{ newDesign: boolean }>`
  margin-bottom: 2.5rem;

  ${({ newDesign }) =>
    newDesign &&
    css`
      @media (min-width: ${breakpoints.lg}) {
        margin-bottom: 1rem;
      }
    `}
`;

export const SectionTitle = styled.strong<{ newPdDesignEnabled: boolean }>`
  ${({ newPdDesignEnabled }) =>
    newPdDesignEnabled
      ? css`
          margin-top: 2.5rem;
          font-size: 1.25rem;
          font-weight: 500;
        `
      : css`
          font-size: 0.9rem;
          line-height: 1.5;

          font-weight: 700;

          @media (min-width: ${breakpoints.md}) {
            font-size: 1.1rem;
          }
        `}
`;

export const CharactericticsWrapper = styled.div<IFullWidth>`
  display: block;
  font-size: 0.9rem;
  flex-basis: 100%;

  margin: 0.5rem 0 1.5rem;

  @media (min-width: ${breakpoints.md}) {
    margin: 0;
    ${({ newPdDesignEnabled }) =>
      newPdDesignEnabled
        ? css`
            flex-basis: 100%;
            @media (min-width: ${breakpoints.lg}) {
              flex-basis: ${(props: IFullWidth) =>
                props.fullWidth ? '100%' : '35%'};
            }

            @media (min-width: ${breakpoints.xl}) {
              flex-basis: ${(props: IFullWidth) =>
                props.fullWidth ? '100%' : '30%'};
            }
          `
        : css`
            flex-basis: ${(props: IFullWidth) =>
              props.fullWidth ? '100%' : '34%'};
          `}

    order: 2;
  }
`;

interface INameProps {
  isTooltip?: boolean;
  newPdDesignEnabled?: boolean;
}

const NameStyles = css`
  font-weight: bold;
  text-decoration: none;
  color: ${(props) => props.theme.color.text.primary};
  border-bottom: ${(props: INameProps) =>
    props.isTooltip ? '1px dashed' : 'none'};
  border-bottom-color: ${theme.color.border.selected};
  display: inline-block;
  width: fit-content;
`;

export const CharacteristicsTable = styled.table<{
  newPdDesignEnabled?: boolean;
}>`
  width: 100%;

  ${({ newPdDesignEnabled }) =>
    newPdDesignEnabled
      ? css`
          border-spacing: 0;
          border: solid 1px ${theme.color.border.divider};
          border-radius: 0.25rem;
          font-family: 'Roboto';

          tr {
            font-size: 0.9rem;

            &:first-of-type {
              ${theme.typography.labelRegular}
            }

            &:nth-child(odd) {
              background-color: ${theme.color.background.disabled};
            }
          }

          td {
            padding: 0.75rem 0.5rem;
            width: 50%;
            text-align: left;

            &:first-child {
              color: ${theme.color.text.secondary};
              text-align: left;
              padding-left: 1rem;
            }

            &:last-child {
              padding-right: 1rem;
            }
          }
        `
      : css`
          display: flex;
          flex-wrap: wrap;

          tr:not(:first-child) {
            margin-top: 1.25rem;
          }
        `};
`;

export const TooltipContentWrapper = styled.div`
  font-weight: 400 !important;
  min-width: 16rem;
`;

export const CharacteristicName = styled.td<INameProps>`
  ${NameStyles}

  div:first-child {
    cursor: ${(props: INameProps) => (props.isTooltip ? 'help' : 'default')};
  }
  ${({ newPdDesignEnabled }) =>
    newPdDesignEnabled &&
    css`
      border: none;
      display: table-cell;
      ${theme.typography.labelRegular400};
    `}
  padding: 0;
`;

export const CharacteristicValue = styled.td<{ newDesign: boolean }>`
  padding: 0;

  ${({ newDesign }) =>
    newDesign
      ? css`
          ${theme.typography.labelRegular400}
        `
      : css`
          font-size: 0.9rem;
        `}
`;

export const CharacteristicRow = styled.tr<IFullWidth>`
  ${({ newPdDesignEnabled }) =>
    !newPdDesignEnabled &&
    css`
      display: block;
      width: 100%;

      @media (min-width: ${breakpoints.md}) {
        width: 100%;
        padding-right: 2.5rem;
      }
    `}
`;

export const CharacteristicsTableWrapper = styled.div`
  margin-bottom: 3rem;
  width: 100%;
`;

export const HairSensitivityTestLink = styled(Link)`
  display: block;
  margin-top: 1.3rem;

  font-size: 0.9rem;
  line-height: 1.5;
  text-decoration: underline;
`;

export const LivestreamsContainer = styled.div<{ newPdDesignEnabled: boolean }>`
  ${({ newPdDesignEnabled }) =>
    newPdDesignEnabled &&
    css`
      @media (min-width: ${breakpoints.lg}) {
        margin-top: 1.5rem;
        padding-right: 2rem;
      }
    `}
`;
